import { z } from 'zod';

/**
 * Field types an intake form may contain. Single source of truth: the
 * discriminated-union field schema below, the GraphQL layer, and the client
 * form renderer all derive from this list. V1 has no conditional logic.
 */
export const INTAKE_FORM_FIELD_TYPES = {
  checkbox: 'checkbox',
  checklist: 'checklist',
  date: 'date',
  fileUpload: 'fileUpload',
  number: 'number',
  select: 'select',
  text: 'text',
  textarea: 'textarea',
} as const;
export const IntakeFormFieldTypeSchema = z.enum(INTAKE_FORM_FIELD_TYPES);
export type IntakeFormFieldType = z.infer<typeof IntakeFormFieldTypeSchema>;

/**
 * Stable, unique-within-form property name used as the key in the submitted
 * values object and as the React Hook Form `register` name. Preserve a field's
 * `name` across label edits so previously submitted values do not drift.
 */
export const IntakeFormFieldNameSchema = z
  .string()
  .min(1)
  .max(200)
  .regex(
    /^[a-zA-Z][a-zA-Z0-9_]*$/,
    'Field name must start with a letter and contain only letters, numbers, or underscores.'
  );

/**
 * ISO calendar-date string (YYYY-MM-DD). Unlike a bare digit-grouping regex,
 * this rejects impossible dates (e.g. 2026-02-31, 2026-13-01). Shared by the
 * date field's validation bounds (here) and the submitted date value schema
 * (in ./values) so the two can never drift.
 */
export const DEFAULT_MIN_LENGTH = 0;
export const DEFAULT_TEXT_FORMAT = '';
export const IsoDateStringSchema = z.iso.date();
export const DEFAULT_FILE_UPLOAD_MAX_FILES = 100;
export const DEFAULT_MAX_LENGTH = Number.MAX_SAFE_INTEGER;
export const DEFAULT_MIN_NUMBER = Number.MIN_SAFE_INTEGER;
export const DEFAULT_MAX_NUMBER = Number.MAX_SAFE_INTEGER;

/** Fields shared by every field type. */
const BaseFieldShape = {
  helpText: z.string().max(2000),
  id: z.string().min(1).max(200),
  label: z.string().min(1).max(500),
  name: IntakeFormFieldNameSchema,
  order: z.number().int().nonnegative(),
  placeholder: z.string().max(500),
  required: z.boolean(),
};

const BaseFieldSchema = z.object({
  ...BaseFieldShape,
  helpText: BaseFieldShape.helpText.default(''),
  order: BaseFieldShape.order.default(0),
  placeholder: BaseFieldShape.placeholder.default(''),
  required: BaseFieldShape.required.default(false),
});

/**
 * A length range is unsatisfiable when min > max, which would make a required
 * field permanently impossible to complete. Reject it at author time.
 */
function lengthRangeIsValid(v: { maxLength: number; minLength: number }): boolean {
  return v.minLength <= v.maxLength;
}
const LENGTH_RANGE_ERROR = 'minLength must be less than or equal to maxLength.';

const TextValidationShape = {
  format: z.enum([DEFAULT_TEXT_FORMAT, 'email']),
  maxLength: z.number().int().nonnegative(),
  minLength: z.number().int().nonnegative(),
};

const TextValidationSchema = z
  .object({
    ...TextValidationShape,
    format: TextValidationShape.format.default(DEFAULT_TEXT_FORMAT),
    maxLength: TextValidationShape.maxLength.default(DEFAULT_MAX_LENGTH),
    minLength: TextValidationShape.minLength.default(DEFAULT_MIN_LENGTH),
  })
  .refine(lengthRangeIsValid, { message: LENGTH_RANGE_ERROR, path: ['minLength'] });

const TextFieldSchema = BaseFieldSchema.extend({
  type: z.literal(INTAKE_FORM_FIELD_TYPES.text),
  validation: TextValidationSchema.default({
    format: DEFAULT_TEXT_FORMAT,
    maxLength: DEFAULT_MAX_LENGTH,
    minLength: DEFAULT_MIN_LENGTH,
  }),
});

const TextareaValidationShape = {
  maxLength: z.number().int().nonnegative(),
  minLength: z.number().int().nonnegative(),
};

const TextareaValidationSchema = z
  .object({
    ...TextareaValidationShape,
    maxLength: TextareaValidationShape.maxLength.default(DEFAULT_MAX_LENGTH),
    minLength: TextareaValidationShape.minLength.default(DEFAULT_MIN_LENGTH),
  })
  .refine(lengthRangeIsValid, { message: LENGTH_RANGE_ERROR, path: ['minLength'] });

const TextareaFieldSchema = BaseFieldSchema.extend({
  type: z.literal(INTAKE_FORM_FIELD_TYPES.textarea),
  validation: TextareaValidationSchema.default({
    maxLength: DEFAULT_MAX_LENGTH,
    minLength: DEFAULT_MIN_LENGTH,
  }),
});

const NumberValidationShape = {
  max: z.number(),
  min: z.number(),
};

const NumberValidationSchema = z
  .object({
    ...NumberValidationShape,
    max: NumberValidationShape.max.default(DEFAULT_MAX_NUMBER),
    min: NumberValidationShape.min.default(DEFAULT_MIN_NUMBER),
  })
  .refine(v => v.min <= v.max, {
    message: 'min must be less than or equal to max.',
    path: ['min'],
  });

const NumberFieldSchema = BaseFieldSchema.extend({
  type: z.literal(INTAKE_FORM_FIELD_TYPES.number),
  validation: NumberValidationSchema.default({
    max: DEFAULT_MAX_NUMBER,
    min: DEFAULT_MIN_NUMBER,
  }),
});

const DateValidationSchema = z
  .object({
    // ISO calendar dates (YYYY-MM-DD), compared lexicographically.
    max: IsoDateStringSchema.optional(),
    min: IsoDateStringSchema.optional(),
  })
  .refine(v => v.min === undefined || v.max === undefined || v.min <= v.max, {
    message: 'min date must be on or before max date.',
    path: ['min'],
  });

const DateFieldSchema = BaseFieldSchema.extend({
  type: z.literal(INTAKE_FORM_FIELD_TYPES.date),
  validation: DateValidationSchema.default({}),
});

const IntakeFormSelectOptionSchema = z.object({
  label: z.string().min(1).max(500),
  value: z.string().min(1).max(500),
});

/** Upper bound on per-field option lists, so a definition can't grow unbounded. */
const MAX_FIELD_OPTIONS = 100;

const SelectFieldSchema = BaseFieldSchema.extend({
  options: z.array(IntakeFormSelectOptionSchema).min(1).max(MAX_FIELD_OPTIONS),
  type: z.literal(INTAKE_FORM_FIELD_TYPES.select),
});

const CheckboxFieldSchema = BaseFieldSchema.extend({
  type: z.literal(INTAKE_FORM_FIELD_TYPES.checkbox),
});

/** A checklist is a multi-select group of options; its value is a string[]. */
const ChecklistFieldSchema = BaseFieldSchema.extend({
  options: z.array(IntakeFormSelectOptionSchema).min(1).max(MAX_FIELD_OPTIONS),
  type: z.literal(INTAKE_FORM_FIELD_TYPES.checklist),
});

const FileUploadValidationShape = {
  maxFiles: z.number().int().positive(),
};

const FileUploadValidationSchema = z.object({
  maxFiles: FileUploadValidationShape.maxFiles.default(DEFAULT_FILE_UPLOAD_MAX_FILES),
});

const FileUploadFieldSchema = BaseFieldSchema.extend({
  type: z.literal(INTAKE_FORM_FIELD_TYPES.fileUpload),
  validation: FileUploadValidationSchema.default({
    maxFiles: DEFAULT_FILE_UPLOAD_MAX_FILES,
  }),
});

export const IntakeFormFieldSchema = z.discriminatedUnion('type', [
  TextFieldSchema,
  TextareaFieldSchema,
  NumberFieldSchema,
  DateFieldSchema,
  SelectFieldSchema,
  CheckboxFieldSchema,
  ChecklistFieldSchema,
  FileUploadFieldSchema,
]);
export type IntakeFormField = z.infer<typeof IntakeFormFieldSchema>;

/**
 * The persisted, AI-readable form definition. Stored as JSONB on both the
 * reusable template and the launched-instance snapshot.
 */
export const IntakeFormDefinitionSchema = z
  .object({
    // Bounded so an API/AI caller can't persist an unbounded definition blob.
    fields: z.array(IntakeFormFieldSchema).max(200),
    version: z.number().int().positive().default(1),
  })
  .superRefine((definition, ctx) => {
    const fieldNames = new Set<string>();
    for (const [index, field] of definition.fields.entries()) {
      if (!fieldNames.has(field.name)) {
        fieldNames.add(field.name);
        continue;
      }

      ctx.addIssue({
        code: 'custom',
        message: 'Field names must be unique within a form.',
        path: ['fields', index, 'name'],
      });
    }
  });
export type IntakeFormDefinition = z.infer<typeof IntakeFormDefinitionSchema>;

/** Shape of a single uploaded file as stored in the values object. */
export const IntakeFormFileValueSchema = z.object({
  documentId: z.uuid(),
  name: z.string().min(1),
});
export type IntakeFormFileValue = z.infer<typeof IntakeFormFileValueSchema>;

/**
 * Loose submitted-values map, keyed by field `name`. Used for the JSONB column
 * validators where the concrete per-field schema is not available; precise
 * validation is done with {@link buildIntakeFormValuesSchema} against a
 * specific definition.
 */
export const IntakeFormValuesSchema = z.record(z.string(), z.unknown());
export type IntakeFormValues = z.infer<typeof IntakeFormValuesSchema>;
